import React from "react";
import { useFormContext } from "react-hook-form";
import { Clipboard, Star, Users } from "lucide-react";
import FormField from "@/components/ui/FormField";
import { Input } from "@/components/ui/input";
import { FileUpload } from "@/components/ui/FileUpload";
import { cn } from "@/utils/cn";
import { ApplicationFormValues } from "@/pages/landing/application/applicationFormSchema";
import programmeImage from "@/assets/images/landing/programme/programme-1.jpg";
import { formatCurrencyAmount } from "@/utils/currency";
import avatar1 from "@/assets/images/landing/avatars/avatar-1.png";
import avatar2 from "@/assets/images/landing/avatars/avatar-2.png";
import avatar3 from "@/assets/images/landing/avatars/avatar-3.png";
import avatar4 from "@/assets/images/landing/avatars/avatar-4.png";

const avatarStrip = [avatar1, avatar2, avatar3, avatar4, avatar3];

interface BankAccountInfo {
  bankName: string;
  accountNumber: string;
  accountName?: string;
  amount?: string;
  currency?: string;
  instructions?: string;
}

interface PaymentInformationFormProps {
  bankDetails: BankAccountInfo;
  programmeName?: string;
  programmePrice?: string;
  programmeImageUrl?: string;
  onReceiptSelect: (files: FileList | null) => void;
  receiptFileName?: string;
  receiptError?: string;
  existingReceiptUrl?: string;
  existingReceiptName?: string;
}

const PaymentInformationForm: React.FC<PaymentInformationFormProps> = ({
  bankDetails,
  programmeName,
  programmePrice,
  programmeImageUrl,
  onReceiptSelect,
  receiptFileName,
  receiptError,
  existingReceiptUrl,
  existingReceiptName,
}) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<ApplicationFormValues>();

  const paymentErrors = errors.payment ?? {};
  const displayAmount =
    programmePrice ??
    formatCurrencyAmount(bankDetails.amount, bankDetails.currency);
  const handleCopy = (text: string) => {
    if (!navigator?.clipboard || !text) return;
    navigator.clipboard.writeText(text).catch(() => {
      // Swallow clipboard issues to avoid interrupting user flow
    }).then(() => {
      alert("Copied to clipboard");
    });
  };

  return (
    <div className="space-y-10">
      <div className="grid gap-10 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-8 text-sm font-medium">
              <span className="text-slate-400 cursor-not-allowed">Card</span>
              <span className="relative pl-4 text-[#7b2a40] font-semibold">
                <span
                  aria-hidden="true"
                  className="absolute left-0 top-1/2 h-8 w-0.5 -translate-y-1/2 rounded bg-[#7b2a40]"
                />
                Bank Transfer
              </span>
            </div>

            <div className="overflow-hidden rounded-[32px] border border-slate-200 shadow-xl">
              <div className="relative h-56 w-full">
                <img
                  src={programmeImageUrl || programmeImage}
                  alt={programmeName ?? "Selected programme"}
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                
                
                <div className="absolute top-0 w-full pt-4 px-8 pb-8 text-left text-white">
                  <div className="flex flex-wrap xl:flex-nowrap items-start justify-between w-full gap-3 mb-2">
                    <h3 className="text-lg font-semibold">
                      {programmeName ?? "Selected Programme"}
                    </h3>
                    {
                      displayAmount &&
                      <div className="min-w-32 rounded-full bg-[#7b2a40] px-4 py-1 text-sm text-center font-semibold text-white shadow-lg text-nowrap">
                        {displayAmount}
                      </div>
                    }
                  </div>
                  
                  <div className="my-3 flex -space-x-2">
                    {avatarStrip.map((avatar, index) => (
                      <img
                        key={index}
                        src={avatar}
                        alt="Past student"
                        className="h-7 w-7 rounded-full border-2 border-white object-cover"
                      />
                    ))}
                  </div>

                  <div className="mt-3 flex items-center gap-3 text-sm text-white/80">
                    <Users className="h-4 w-4" />
                    <span>Join over 10k students around the world</span>
                  </div>

                  <div className="mt-3 flex items-center gap-1">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <Star
                        key={index}
                        className="h-4 w-4 fill-yellow-400 text-yellow-400"
                      />
                    ))}
                    <span className="ml-2 text-sm font-medium">5.0 rating</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-6 rounded-[28px] border border-slate-200 bg-white p-8 shadow-sm">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-[#7b2a40]">
              Bank Transfer Details
            </p>
            <p className="mt-1 text-sm text-slate-600">
              Complete your transfer with the details below, then upload your
              receipt for confirmation.
            </p>
          </div>

          <div className="space-y-5">
            <div className="rounded-2xl border border-slate-200 p-4">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
                Bank Name
              </p>
              <div className="mt-2 flex items-center justify-between gap-2">
                <p className="text-base font-semibold text-slate-900">
                  {bankDetails.bankName}
                </p>
                <button
                  type="button"
                  onClick={() => handleCopy(bankDetails.bankName)}
                  className="rounded-full p-1.5 text-slate-500 transition hover:bg-[#7b2a40]/10 hover:text-[#7b2a40]"
                  aria-label="Copy bank name"
                >
                  <Clipboard className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 p-4">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
                Account Number
              </p>
              <div className="mt-2 flex items-center justify-between gap-2">
                <p className="text-base font-semibold text-slate-900">
                  {bankDetails.accountNumber}
                </p>
                <button
                  type="button"
                  onClick={() => handleCopy(bankDetails.accountNumber)}
                  className="rounded-full p-1.5 text-slate-500 transition hover:bg-[#7b2a40]/10 hover:text-[#7b2a40]"
                  aria-label="Copy account number"
                >
                  <Clipboard className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 p-4">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
                Account Name
              </p>
              <p className="mt-2 text-base font-semibold text-slate-900">
                {bankDetails.accountName || "Kaduna Business School"}
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 p-4">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
                Amount Payable
              </p>
              <p className="mt-2 text-base font-semibold text-slate-900">
                {displayAmount || "Refer to programme fee"}
              </p>
            </div>
          </div>

          {bankDetails.instructions && (
            <div className="rounded-2xl border border-dashed border-[#7b2a40]/30 bg-[#7b2a40]/5 p-4 text-sm text-slate-600">
              {bankDetails.instructions}
            </div>
          )}

          <div className="grid gap-6 md:grid-cols-2">
            <FormField label="Transaction Reference" required>
              <Input
                placeholder="Bank transfer reference"
                {...register("payment.reference")}
                className={cn(
                  paymentErrors.reference &&
                    "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500",
                )}
              />
              {paymentErrors.reference && (
                <p className="mt-1 text-xs text-red-600">
                  {paymentErrors.reference.message as string}
                </p>
              )}
            </FormField>
          </div>

          <FileUpload
            label="Upload Receipt"
            className="bg-[#7b2a40]/5 text-[#7b2a40] hover:bg-[#7b2a40]/10"
            helperText={
              <div className="space-y-1">
                <p>Upload in .pdf, .jpeg or .png</p>
                {receiptFileName && (
                  <p className="text-xs text-slate-500">Selected: {receiptFileName}</p>
                )}
                {receiptError && (
                  <p className="text-xs text-red-600">{receiptError}</p>
                )}
                {existingReceiptUrl && (
                  <p className="text-xs text-slate-500">
                    Previously uploaded:{" "}
                    <a
                      href={existingReceiptUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#7b2a40] underline"
                    >
                      {existingReceiptName ?? "View document"}
                    </a>
                  </p>
                )}
              </div>
            }
            accept=".pdf,.jpeg,.jpg,.png"
            onFileSelect={onReceiptSelect}
          />
        </div>
      </div>
    </div>
  );
};

export default PaymentInformationForm;
